/* tslint:disable:no-console */
import * as Sentry from '@sentry/browser'
import Axios from 'axios'
import { Button, Notification } from 'bloomer'
import { PureComponent } from 'react'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { answersBeingSaved, pendingResultsCount } from '../offlineMode/pendingResults'
import { IAlphaprocessState } from '../store'
import { requestsInFlight, trackRequestsInFlight } from '../util/requestsInFlight'

import ITranslationProps from '../types/translationProps'
import LoadSpinner from './loadSpinner/LoadSpinner'
import Overlay from './widgets/Overlay'

interface IState {
  newVersionAvailable: boolean
  timer: NodeJS.Timeout
  canceled: boolean
  isLoading: boolean
}

const interval = 5 * 60 * 1000
// a tab in the background this long checks again when it comes back
const awayMs = 30 * 60 * 1000

interface Props extends ITranslationProps, RouteComponentProps {
  alwaysVisible?: boolean
  isOffline: boolean
}

const wait = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms) })

// resolves when a newer worker is active, or right away when the server has the same one
async function updateWorker(registration: ServiceWorkerRegistration) {
  await registration.update()
  const next = registration.installing ?? registration.waiting
  if (!next) return
  await new Promise<void>((resolve) => {
    const done = () => ['activated', 'redundant'].includes(next.state)
    if (done()) resolve()
    next.addEventListener('statechange', () => { if (done()) resolve() })
  })
}

// the process a page belongs to (fill-in page, its areas): moving between its areas is no moment to reload
const processOf = (pathname: string) => pathname.match(/\/audit\/(\d+)/)?.[1] ?? null

/**
 * This component checks if a new version is available.
 * A request is made to /version every five minutes and when the tab comes back to the foreground.
 * The result is then compared to REACT_APP_COMMIT_SHA.
 * A newer version is loaded by itself (old tabs and home-screen apps otherwise keep running old code
 * that no longer fits the backend): after a page change, or when the tab comes back after a while,
 * and only when nothing is lost by it: online, not in offline mode, no answer, photo or other request
 * on its way and none waiting to be sent, not while moving between the areas of one process.
 */
class VersionChecker extends PureComponent<Props, IState> {
  public state: IState = {
    newVersionAvailable: false,
    canceled: false,
    timer: null,
    isLoading: false
  }

  private stopListening: (() => void) | null = null

  private hiddenAt = 0

  private reloadTimer: ReturnType<typeof setTimeout> | null = null

  private newVersion = ''

  // one automatic reload per new version and tab: should the reload still bring the old version (a
  // cache), it doesn't loop; the button stays
  private alreadyReloadedFor = () => {
    try {
      return sessionStorage.getItem('version-reload') === this.newVersion
    } catch {
      return true
    }
  }

  private canReloadNow = () => this.state.newVersionAvailable && !this.state.isLoading && !this.alreadyReloadedFor()
    && navigator.onLine && !this.props.isOffline
    && requestsInFlight() === 0 && answersBeingSaved() === 0 && pendingResultsCount() === 0

  // a moment later (a save started by leaving the page has begun by then), if still nothing is lost
  private reloadSoonIfSafe = () => {
    if (this.reloadTimer) clearTimeout(this.reloadTimer)
    this.reloadTimer = setTimeout(() => {
      if (!this.canReloadNow()) return
      try {
        sessionStorage.setItem('version-reload', this.newVersion)
      } catch {
        return
      }
      this.handleReload()
    }, 1500)
  }

  private onNavigate = (from: string, to: string) => {
    const process = processOf(to)
    if (process && process === processOf(from)) return
    this.reloadSoonIfSafe()
  }

  private onVisibilityChange = async () => {
    if (document.visibilityState === 'hidden') {
      this.hiddenAt = Date.now()
      return
    }
    const wasAway = this.hiddenAt > 0 && Date.now() - this.hiddenAt > awayMs
    await this.checkForNewVersion()
    if (wasAway && !processOf(this.props.location.pathname)) this.reloadSoonIfSafe()
  }

  public checkForNewVersion = async () => {
    if ('cordova' in window) {
      return
    }

    try {
      const result = await Axios.get<string>('/version')
      if (typeof result.data !== 'string') {
        return
      }

      const currentVersion = process.env.REACT_APP_COMMIT_SHA?.trim()
      const newVersion = result.data.trim()

      if (!currentVersion || newVersion.startsWith('<')) {
        return
      }

      if (currentVersion !== newVersion) {
        this.newVersion = newVersion
        this.setState({ newVersionAvailable: true })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error checking for new version', error)
    }
  }

  // the app worker (src/serviceWorker/OneSignalSDKWorker.ts) caches the files of one build: fetch the new worker first, it takes over at
  // once, then reload, so the reload brings the new build and not the cached one. Its caches stay (it
  // removes outdated ones itself), they are what lets the app start without network. Waits 8 s at most.
  private handleReload = async () => {
    this.setState({ isLoading: true })
    try {
      const registration = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined
      if (registration) await Promise.race([updateWorker(registration), wait(8000)])
    } catch (error) {
      // offline or the worker failed: the reload still brings the new build from the network
      Sentry.captureException(error)
    }
    window.location.reload()
  }

  public async componentDidMount() {
    (window as any).versionChecker = this
    if (process.env.NODE_ENV === 'production') {
      await this.checkForNewVersion()
      const timer = setInterval(this.checkForNewVersion, interval)
      this.setState({ timer })
      // the copy in the profile footer only shows the button
      if (!this.props.alwaysVisible) {
        trackRequestsInFlight()
        let from = this.props.location.pathname
        this.stopListening = this.props.history.listen((location) => {
          this.onNavigate(from, location.pathname)
          from = location.pathname
        })
        document.addEventListener('visibilitychange', this.onVisibilityChange)
      }
    }
  }

  public componentWillUnmount() {
    if (this.state.timer) {
      clearInterval(this.state.timer)
    }
    if (this.reloadTimer) clearTimeout(this.reloadTimer)
    this.stopListening?.()
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }

  public render() {
    const { t, alwaysVisible } = this.props

    if (this.state.canceled) {
      return null
    }

    if (this.state.newVersionAvailable || alwaysVisible) {
      return (
        <Notification isColor="info">
          <p>
            { t('InfoNewVersionAvailable') }
          </p>
          <Button onClick={this.handleReload}>{ t('ReloadNow') }</Button>
          { this.state.isLoading && (
          <Overlay>
            {' '}
            <LoadSpinner className="centered" />
            {' '}
          </Overlay>
          ) }
        </Notification>
      )
    }

    return null
  }
}

const mapStateToProps = ({ core }: IAlphaprocessState) => ({ isOffline: !!core.isOffline })

export default withRouter(connect(mapStateToProps)(withTranslation()(VersionChecker)))
